import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { AdvancedMarker } from '@vis.gl/react-google-maps'
import { Box, useTheme, SvgIcon } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { COMMON } from 'theme/colors'
import { upperCase } from 'lodash'
import { isValidHex } from 'utils/validations'
import { common } from '@mui/material/colors'

// Import SVG Assets
import { ReactComponent as SideLoadIcon } from 'assets/methods/SideLoad.svg'
import { ReactComponent as FrontLoadIcon } from 'assets/methods/FrontLoad.svg'
import { ReactComponent as RearLoadIcon } from 'assets/methods/RearLoad.svg'
import { ReactComponent as DumpTruckIcon } from 'assets/methods/DumpTruck.svg'
import { ReactComponent as HookIcon } from 'assets/methods/Hook.svg'
import { ReactComponent as HoistIcon } from 'assets/methods/Hoist.svg'
import { ReactComponent as GrappleIcon } from 'assets/methods/Grapple.svg'

const METHOD_ICONS = {
  'SIDE LOAD': SideLoadIcon,
  'FRONT LOAD': FrontLoadIcon,
  'REAR LOAD': RearLoadIcon,
  'DUMP TRUCK': DumpTruckIcon,
  HOOK: HookIcon,
  HOIST: HoistIcon,
  GRAPPLE: GrappleIcon,
}

const DriverMarker = ({ position, onClick, color, method, zIndex = 2000, onMouseEnter, onMouseLeave }) => {
  const theme = useTheme()

  if (!position) return null

  const bgColor = isValidHex(color) ? color : common.black
  const contrastColor = theme.palette.getContrastText(bgColor)

  const IconComponent = useMemo(() => {
    if (!method) return SideLoadIcon
    const normalizedMethod = upperCase(method)
    return METHOD_ICONS[normalizedMethod] || SideLoadIcon
  }, [method])

  return (
    <AdvancedMarker position={position} zIndex={zIndex} onClick={onClick}>
      <Box
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `2px solid ${COMMON.white}`,
          boxShadow: `0 0 12px 2px ${alpha(bgColor, 0.6)}`, // Gradient glow
          cursor: 'pointer',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'scale(1.1)',
            zIndex: 30,
          },
        }}
      >
        <SvgIcon component={IconComponent} sx={{ color: contrastColor, fontSize: 18 }} inheritViewBox />
      </Box>
    </AdvancedMarker>
  )
}

DriverMarker.propTypes = {
  position: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  onClick: PropTypes.func,
  color: PropTypes.string,
  method: PropTypes.string,
  zIndex: PropTypes.number,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
}

export default React.memo(DriverMarker) // Added React.memo here as well for good measure
